import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { setPreferenceIndicesCalcs } from 'IndicesApp/ts/apps/index-family-app/state/family-indices-table/family-indices-table.action';

import { ASC, DESC } from 'Angular/constants/core.constants';
import {
    INITIAL_PAGINATION_STATE,
    PaginationControls,
} from 'Angular/features/components/pagination/pagination.constants';
import { DEFAULT_CALC_COLUMNS } from 'IndicesApp/ts/apps/index-family-app/constants';
import { IndexFamilyRootStore } from '../../state/interfaces/index-family-root-store.interface';
import { UserPreference } from 'AccountsApp/ts/interfaces/user-preference.interface';
import { PaginationInfo } from 'Angular/features/components/pagination/interfaces/pagination-info.interface';
import { SortColumnAndDirection } from 'Angular/interfaces/sort-column-and-direction.interface';
import { SortDirectionCssClasses } from 'Angular/utils/sort/interfaces/sort-direction-css-classes.interface';
import {
    IndexPerformanceCalcsRowData,
    IndexPerformanceCalcsSortableItem,
    IndicesDataColumnInfo,
    IndicesPagePerformanceGetViewResponse,
    IndicesPerformanceCalcData,
} from 'IndicesApp/ts/interfaces/indices-view-responses.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { IndicesService } from 'IndicesApp/ts/shared/services/indices.service';

import { PaginationControlsComponent } from 'Angular/features/components/pagination/pagination-controls/pagination-controls.component';
import equal from 'Angular/utils/equal/deep-equal';
import {
    getNewSortDirectionCssClasses,
    getSortColumnAndDirection,
    initializeSortDirectionCssClasses,
    sortTableItems,
} from 'Angular/utils/sort/table-sort';
import { getColumnHeaderClass } from 'IndicesApp/ts/shared/utils/utils';

@Component({
    standalone: false,
    selector: 'ycn-family-indices-table',
    templateUrl: './family-indices-table.template.html',
})
export class FamilyIndicesTableComponent implements OnInit, OnDestroy {
    @Input() indexFamily!: string;

    @Output() pageChangeHandler = new EventEmitter<number>();

    @ViewChild(PaginationControlsComponent) paginationControlsComponent!: PaginationControlsComponent;

    private fetchSubscription: Subscription | null = null;

    private storeSubscription: Subscription;

    NAME_COLUMN: string = 'name';

    SYMBOL_COLUMN: string = 'symbol';

    BASE_COLUMN_INFO: IndicesDataColumnInfo[] = [
        {
            metricDisplayName: 'Symbol',
            metricInternalName: this.SYMBOL_COLUMN,
            headerClass: 'text-left',
        },
        {
            metricDisplayName: 'Name',
            metricInternalName: this.NAME_COLUMN,
            headerClass: 'text-left',
        },
    ];

    PAGE_MAX: number = 25;

    paginationInfo: PaginationInfo = INITIAL_PAGINATION_STATE;

    sortColumnAndDirection: SortColumnAndDirection<string>;

    sortDirectionCssClasses: SortDirectionCssClasses;

    indexFamilyTableData: IndicesPerformanceCalcData[] = [];

    sortedTableRows: IndexPerformanceCalcsSortableItem[] = [];

    currentIndicesShown: IndexPerformanceCalcsSortableItem[] = [];

    columnsInfo: IndicesDataColumnInfo[] = [];

    columnNames: string[] = [];

    pageMappings: { [id: number]: IndexPerformanceCalcsSortableItem[] } = {};

    loading: boolean = true;

    rawSortBy: string = '';

    noData: boolean = true;

    constructor(
        private indicesService: IndicesService,
        private accountsService: AccountsService,
        private readonly store: Store<{ reducers: IndexFamilyRootStore }>
    ) {
        this.storeSubscription = this.store.select('reducers').subscribe(this.mapStateToComponent);
        this.sortColumnAndDirection = {
            sortColumn: '',
            sortDirection: DESC,
        };
        const sortColumns = [this.SYMBOL_COLUMN, this.NAME_COLUMN];
        this.sortDirectionCssClasses = initializeSortDirectionCssClasses(sortColumns);
        this.paginationInfo.current_page_num = 1;
    }

    ngOnInit(): void {
        this.accountsService.getUserPreferences().subscribe({
            next: (preference: UserPreference) => {
                this.columnNames = preference.indices_page_family_indices_columns;
                this.store.dispatch(setPreferenceIndicesCalcs({ calcs: this.columnNames }));
                this.fetchSubscription = this.getTableData();
            },
            error: () => {
                this.columnNames = DEFAULT_CALC_COLUMNS.map((calc) => calc.metricInternalName);
                this.store.dispatch(setPreferenceIndicesCalcs({ calcs: this.columnNames }));
                this.fetchSubscription = this.getTableData();
            },
        });
    }

    ngOnDestroy(): void {
        if (this.fetchSubscription !== null) {
            this.fetchSubscription.unsubscribe();
        }
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: IndexFamilyRootStore): void => {
        const currColumns = state.preferenceIndicesCalcs;
        const prevColumns = this.columnNames;
        if (!equal(currColumns, prevColumns)) {
            // Check to see if there is already a fetch pending. If there is we need
            // "cancel" the request by unsubscribing from it before creating a new
            // subscription.
            if (this.fetchSubscription !== null) {
                this.fetchSubscription.unsubscribe();
            }
            this.columnNames = state.preferenceIndicesCalcs;
            this.fetchSubscription = this.getTableData();
        }
    };

    getTableData(): Subscription {
        this.loading = true;
        return this.indicesService.getIndicesData(this.indexFamily, this.columnNames).subscribe({
            next: (response: IndicesPagePerformanceGetViewResponse) => {
                this.indexFamilyTableData = response.indicesData;
                this.columnsInfo = this.BASE_COLUMN_INFO.concat(response.columnInfo);
                this.noData = false;
                this.noData = this.indexFamilyTableData.length === 0;
                // format calcs data to enable sorting
                this.sortedTableRows = this.formatData(this.indexFamilyTableData);
                this.resetSortBy(this.columnNames);
                this.initializePagination();
                this.loading = false;
            },
            error: () => {
                this.noData = true;
                this.loading = false;
            },
        });
    }

    formatData(calcsData: IndicesPerformanceCalcData[]): IndexPerformanceCalcsSortableItem[] {
        return calcsData.map((calcData) => {
            const indexFamilyPerformanceData: IndexPerformanceCalcsSortableItem = {};
            // manually add symbol & name because they are non-calc columns
            indexFamilyPerformanceData[this.SYMBOL_COLUMN] = calcData.label;
            indexFamilyPerformanceData[this.NAME_COLUMN] = calcData.value;

            // contains data needed in the template but cannot be sorted
            const rowData: IndexPerformanceCalcsRowData = { url: calcData.url, displayName: calcData.value };

            // add <calc base name, calc raw value> mapping to indexFamilyPerformanceData -- we want
            // to sort by raw value because the table-sort tool does not sort the formatted strings correctly
            calcData.data.forEach((calc) => {
                indexFamilyPerformanceData[calc.calc_name] = calc.calc_raw_value;
                rowData[calc.calc_name] = calc.calc_value;
            });
            indexFamilyPerformanceData.rowData = rowData;
            return indexFamilyPerformanceData;
        });
    }

    resetSortBy(columns: string[]): void {
        this.sortColumnAndDirection = {
            sortColumn: '',
            sortDirection: DESC,
        };
        this.sortDirectionCssClasses = initializeSortDirectionCssClasses([
            this.SYMBOL_COLUMN,
            this.NAME_COLUMN,
            ...columns,
        ]);
        this.setSortBy(this.NAME_COLUMN);
    }

    setSortBy(newSortColumn: string): void {
        const prevSortColumn: string = this.sortColumnAndDirection.sortColumn;
        this.sortColumnAndDirection = getSortColumnAndDirection<string>(
            {},
            prevSortColumn,
            newSortColumn,
            this.sortColumnAndDirection.sortDirection
        );

        if (newSortColumn !== this.NAME_COLUMN && newSortColumn !== this.SYMBOL_COLUMN) {
            // display empty values at the bottom of the table by treating them as pos/neg infinity
            const nullValueModifier: number =
                this.sortColumnAndDirection.sortDirection === ASC ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
            this.sortedTableRows.forEach((item: IndexPerformanceCalcsSortableItem) => {
                if (!item.rowData[newSortColumn]) {
                    // eslint-disable-next-line no-param-reassign
                    item[newSortColumn] = nullValueModifier;
                }
            });
        }

        this.sortDirectionCssClasses = getNewSortDirectionCssClasses<string>(
            this.sortDirectionCssClasses,
            prevSortColumn,
            newSortColumn,
            this.sortColumnAndDirection.sortDirection
        );

        this.sortedTableRows = sortTableItems<IndexPerformanceCalcsSortableItem, string>(
            this.sortedTableRows,
            this.sortColumnAndDirection
        );
        this.paginationControlsComponent.pageChanged(PaginationControls.FIRST); // Sets currPage to 1 and disables prev/first buttons
        this.initializePagination();
    }

    initializePagination(): void {
        // initializes pageMappings dict for (key: value) (page_num: rows) pagination
        // 1-based to follow paginationInfo protocol
        this.paginationInfo.num_items = this.sortedTableRows.length;
        this.paginationInfo.num_pages = Math.floor(this.sortedTableRows.length / this.PAGE_MAX) + 1;
        for (let _i = 0; _i < this.paginationInfo.num_pages; _i += 1) {
            const start = _i * this.PAGE_MAX;
            let end = start + this.PAGE_MAX;
            if (end > this.sortedTableRows.length) {
                end = this.sortedTableRows.length;
            }
            this.pageMappings[_i + 1] = this.sortedTableRows.slice(start, end);
        }
        this.getCurrentRows();
    }

    getCurrentRows(): void {
        this.currentIndicesShown = this.pageMappings[this.paginationInfo.current_page_num];
        this.paginationInfo.start_index = this.PAGE_MAX * (this.paginationInfo.current_page_num - 1) + 1;
        this.paginationInfo.end_index = this.paginationInfo.start_index - 1 + this.currentIndicesShown.length;
    }

    pageChanged(newPageNum: number): void {
        this.paginationInfo.current_page_num = newPageNum;
        this.getCurrentRows();
        this.pageChangeHandler.emit(newPageNum);
    }

    getHeaderClass(calc: IndicesDataColumnInfo): string {
        return getColumnHeaderClass(this.sortDirectionCssClasses, calc);
    }
}
